import React, { useMemo, useState } from 'react';
import { CalendarOffIcon } from 'lucide-react';
import { AppShell } from '../../components/layout/AppShell';
import { Card, EmptyState, Metric } from '../../components/ui/Panels';
import { ButtonLink } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { BookingCard } from '../../components/bookings/BookingCard';
import { useSession } from '../../contexts/SessionContext';
import { useViewerRole } from '../../hooks/useViewerRole';
import { useAppData } from '../../contexts/AppDataContext';
import { useLocale } from '../../contexts/LocaleContext';
import { currency } from '../../utils/format';
import { chefEarnings, customerSpend } from '../../data/stats';
import type { Booking } from '../../types/booking';
const OPEN: Booking['status'][] = ['requested', 'quoted', 'confirmed', 'in_progress'];
export function Bookings() {
  const {
    chef,
    customer: me
  } = useSession();
  const {
    bookings
  } = useAppData();
  const {
    locale,
    currencyCode
  } = useLocale();
  const [tab, setTab] = useState('upcoming');
  const role = useViewerRole();
  const mine = useMemo(() => bookings.filter(b => role === 'chef' ? b.chefId === chef.id : b.customerId === me.id).sort((a, b) => b.date.localeCompare(a.date)), [bookings, role, chef.id, me.id]);
  const upcoming = mine.filter(b => OPEN.includes(b.status));
  const past = mine.filter(b => b.status === 'completed');
  const cancelled = mine.filter(b => b.status === 'cancelled' || b.status === 'declined');
  const shown = tab === 'upcoming' ? upcoming : tab === 'past' ? past : cancelled;
  const completedTotal = past.reduce((sum, b) => sum + (b.total ?? 0), 0);
  const series = role === 'chef' ? chefEarnings : customerSpend;
  const thisMonth = series[series.length - 1];
  const avg = past.length ? Math.round(completedTotal / past.length) : 0;
  return <AppShell title={role === 'chef' ? 'Bookings' : 'My bookings'} subtitle={`${upcoming.length} open · ${past.length} completed`} actions={role === 'chef' ? undefined : <ButtonLink to="/discover" variant="action" size="sm" data-id="element-2399" data-mp="3rz87fiy5yny6">Find a chef</ButtonLink>} data-id="element-2398" data-mp="2o0co6ofgyzhn">
      <Card className="grid gap-6 p-5 sm:grid-cols-3" data-id="element-2400" data-mp="3nxebfnywy84c">
        <Metric label={role === 'chef' ? 'Earned this month' : 'Spent this month'} value={currency(thisMonth.amount, locale, currencyCode)} detail={`${thisMonth.bookings} bookings in ${thisMonth.month}`} tone="primary" data-id="element-2401" data-mp="3o00jdoppupn3" />
        <Metric label={role === 'chef' ? 'Lifetime bookings' : 'Dinners hosted'} value={String(past.length)} detail={`${currency(completedTotal, locale, currencyCode)} in total`} data-id="element-2402" data-mp="fje16nba9eur" />
        <Metric label="Average booking" value={avg ? currency(avg, locale, currencyCode) : '—'} detail="across completed bookings" data-id="element-2403" data-mp="5s1c23gvd2sf" />
      </Card>

      <Tabs className="mt-7" value={tab} onChange={setTab} items={[{
      id: 'upcoming',
      label: 'Upcoming',
      count: upcoming.length
    }, {
      id: 'past',
      label: 'Completed',
      count: past.length
    }, {
      id: 'cancelled',
      label: 'Cancelled',
      count: cancelled.length
    }]} data-id="element-2404" data-mp="ygfa2j7yjz3z" />

      {shown.length ? <ul className="mt-5 space-y-3" data-id="element-2405" data-mp="1fsdsl0ojkzl7">
          {shown.map(booking => <BookingCard key={booking.id} booking={booking} role={role} data-id="element-2406" data-mp="1jtfuxqdr9rre" />)}
        </ul> : <div className="mt-5" data-id="element-2407" data-mp="1oqphmy9rfpbj">
          <EmptyState icon={<CalendarOffIcon className="h-5 w-5" aria-hidden="true" data-id="element-2409" data-mp="1tpah2vh6u4yx" />} title="Nothing here" body={tab === 'upcoming' ? role === 'chef' ? 'No open requests or confirmed dates right now.' : 'You have no upcoming dinners booked.' : 'This list fills up as bookings close out.'} action={role === 'chef' || tab !== 'upcoming' ? undefined : <ButtonLink to="/discover" data-id="element-2410" data-mp="2ce7br0ghyzho">Find a chef</ButtonLink>} data-id="element-2408" data-mp="1tq2d27ep64pq" />
        </div>}
    </AppShell>;
}